import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Box, Card, CardContent, Typography, Chip, Button, ButtonGroup, Divider, Grid } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAdvertisementByIdApi, getAdvertisementAnalyticsApi } from "../../../redux/api/api";
import { MAIN_URL } from "../../../constant";
import { KpiRow, TrendChart, RANGE_PRESETS, rangeForPreset } from "./AdPerformance";

const STATUS_COLORS = {
  DRAFT: "default",
  SCHEDULED: "info",
  ACTIVE: "success",
  PAUSED: "warning",
  EXPIRED: "error",
  COMPLETED: "secondary",
  ARCHIVED: "default",
};

/** Lifetime-of-campaign performance with a 7/30/90-day range switch. */
const CampaignPerformance = ({ campaignId }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    getAdvertisementAnalyticsApi({ ...rangeForPreset(days), campaign: campaignId })
      .then((res) => setData(res?.data?.info))
      .catch(() => toast.error("Unable to load campaign performance."));
  }, [campaignId, days]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle2">Performance</Typography>
          <ButtonGroup size="small">
            {RANGE_PRESETS.map((p) => (
              <Button key={p.days} variant={days === p.days ? "contained" : "outlined"} onClick={() => setDays(p.days)}>
                {p.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        {data ? (
          <>
            <KpiRow totals={data.totals} reachPartial={data.reachPartial} />
            <TrendChart daily={data.daily} height={240} />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const InfoBlock = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {children ?? "—"}
    </Typography>
  </Box>
);

const AdvertisementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdvertisementByIdApi(id)
      .then((res) => setCampaign(res?.data?.info))
      .catch(() => toast.error("Unable to load campaign."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Box sx={{ p: 4 }}>Loading…</Box>;
  if (!campaign) return <Box sx={{ p: 4 }}>Campaign not found.</Box>;

  const t = campaign.targeting || {};
  const locationLabel =
    t.mode === "ALL"
      ? "All Locations"
      : t.mode === "RADIUS"
        ? t.radius?.center
          ? `${t.radius.radiusKm} km around ${t.radius.label || `${t.radius.center.coordinates[1]}, ${t.radius.center.coordinates[0]}`}`
          : "No point selected"
        : [
            (t.stateIds || []).map((s) => s.name).join(", "),
            (t.cityIds || []).map((c) => c.name).join(", "),
          ]
            .filter(Boolean)
            .join(" → ") || "Unrestricted";

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>{campaign.name}</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>Marketing</li>
          <li>
            <Link to="/marketing/advertisements">Advertisements</Link>
          </li>
          <li>{campaign.campaignId}</li>
        </ul>
      </div>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {campaign.name}
          </Typography>
          <Chip label={campaign.computedStatus || campaign.status} color={STATUS_COLORS[campaign.computedStatus || campaign.status] || "default"} />
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/marketing/advertisements/${campaign._id}/edit`, { state: campaign })}
        >
          Edit Campaign
        </Button>
      </Box>

      <CampaignPerformance campaignId={campaign._id} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Creative Preview
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {["en", "mr", "hi"].map((lang) =>
                  campaign.creative?.[lang]?.imageUrl || campaign.content?.[lang]?.title ? (
                    <Box key={lang} sx={{ width: 220, border: "1px solid #eee", borderRadius: 2, overflow: "hidden" }}>
                      {campaign.creative?.[lang]?.imageUrl ? (
                        <Box component="img" src={campaign.creative[lang].imageUrl} sx={{ width: "100%", height: 110, objectFit: "cover" }} />
                      ) : (
                        <Box sx={{ height: 110, bgcolor: "#f5f5f5" }} />
                      )}
                      <Box sx={{ p: 1.5 }}>
                        <Chip size="small" label={lang.toUpperCase()} sx={{ mb: 0.5 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {campaign.content?.[lang]?.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.content?.[lang]?.subtitle}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null,
                )}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Targeting Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <InfoBlock label="Service">
                    {campaign.source?.type === "THIRD_PARTY" ? "No category (third-party)" : campaign.serviceId}
                  </InfoBlock>
                </Grid>
                <Grid size={6}>
                  <InfoBlock label="Placement">{campaign.placement?.replace(/_/g, " ")}</InfoBlock>
                </Grid>
                <Grid size={12}>
                  <InfoBlock label="Location">{locationLabel}</InfoBlock>
                </Grid>
                {t.areas?.length ? (
                  <Grid size={12}>
                    <InfoBlock label="Areas">{t.areas.join(", ")}</InfoBlock>
                  </Grid>
                ) : null}
                {t.pincodes?.length ? (
                  <Grid size={12}>
                    <InfoBlock label="Pincodes">{t.pincodes.join(", ")}</InfoBlock>
                  </Grid>
                ) : null}
                <Grid size={6}>
                  <InfoBlock label="Language">
                    {t.languages?.length ? t.languages.join(", ").toUpperCase() : "All Languages"}
                  </InfoBlock>
                </Grid>
                <Grid size={6}>
                  <InfoBlock label="Priority">{campaign.priority}</InfoBlock>
                </Grid>
                <Grid size={6}>
                  <InfoBlock label="Audience">{t.customerSegment || "ALL"}</InfoBlock>
                </Grid>
                <Grid size={6}>
                  <InfoBlock label="Subcategories">
                    {(campaign.subcategoryIds || []).map((s) => s.subcategory_name).join(", ") || "Any"}
                  </InfoBlock>
                </Grid>
                {t.districts?.length ? (
                  <Grid size={12}>
                    <InfoBlock label="Districts">{t.districts.join(", ")}</InfoBlock>
                  </Grid>
                ) : null}
                <Grid size={12}>
                  <InfoBlock label="Click Action">
                    {(campaign.action?.type || "SERVICE").replace(/_/g, " ")}
                    {campaign.action?.value ? ` — ${campaign.action.value}` : ""}
                    {campaign.action?.type === "EXTERNAL_URL"
                      ? ` (${campaign.action.openIn === "IN_APP_BROWSER" ? "in-app browser" : "phone browser"}${campaign.action.appendUtm === false ? "" : ", UTM tagged"})`
                      : ""}
                  </InfoBlock>
                </Grid>
                <Grid size={12}>
                  <InfoBlock label="Delivery">
                    Weight {campaign.delivery?.weight || 1}
                    {campaign.delivery?.frequencyCapPerDay ? ` · max ${campaign.delivery.frequencyCapPerDay}/customer/day` : ""}
                    {campaign.delivery?.isFallback ? " · fallback" : ""}
                    {campaign.budget?.maxImpressions ? ` · stop at ${campaign.budget.maxImpressions} impressions` : ""}
                    {campaign.budget?.maxClicks ? ` · stop at ${campaign.budget.maxClicks} clicks` : ""}
                    {campaign.budget?.pricingModel && campaign.budget.pricingModel !== "NONE"
                      ? ` · ${campaign.budget.pricingModel} ₹${campaign.budget.rate || 0}${campaign.budget.totalBudget ? `, budget ₹${campaign.budget.totalBudget}` : ""}`
                      : ""}
                    {campaign.delivery?.exhaustedAt ? ` · limit reached ${new Date(campaign.delivery.exhaustedAt).toLocaleString()}` : ""}
                  </InfoBlock>
                </Grid>
                <Grid size={12}>
                  <InfoBlock label="Schedule">
                    {campaign.schedule?.startAt ? new Date(campaign.schedule.startAt).toLocaleString() : "—"} →{" "}
                    {campaign.schedule?.endAt ? new Date(campaign.schedule.endAt).toLocaleString() : "—"}
                  </InfoBlock>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          {campaign.source?.type === "THIRD_PARTY" ? (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Advertiser
                </Typography>
                <InfoBlock label="Name">{campaign.source.advertiser?.name}</InfoBlock>
                <InfoBlock label="Website">{campaign.source.advertiser?.website || "—"}</InfoBlock>
                <InfoBlock label="Contact">
                  {[campaign.source.advertiser?.contactName, campaign.source.advertiser?.contactEmail, campaign.source.advertiser?.contactPhone]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </InfoBlock>
                {campaign.postbackToken ? (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Conversion postback URL
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                      Share with the partner. The app adds <code>bo_click_id</code> to their landing URL; they call this (server to
                      server) with that value when the customer converts. Use the public API Gateway host
                      (…/api/v1/core/app/ads/postback) in production.
                    </Typography>
                    {(() => {
                      const postbackUrl = `${MAIN_URL}/api/app/ads/postback?campaign=${campaign.campaignId}&token=${campaign.postbackToken}&click_id={bo_click_id}`;
                      return (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                          <Typography variant="caption" sx={{ wordBreak: "break-all", fontFamily: "monospace", flex: 1 }}>
                            {postbackUrl}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => navigator.clipboard.writeText(postbackUrl).then(() => toast.success("Copied."))}
                          >
                            Copy
                          </Button>
                        </Box>
                      );
                    })()}
                  </>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Audit Information
              </Typography>
              <InfoBlock label="Created By">{campaign.createdBy?.name || campaign.createdBy?.email}</InfoBlock>
              <InfoBlock label="Created At">{campaign.createdAt ? new Date(campaign.createdAt).toLocaleString() : "—"}</InfoBlock>
              <InfoBlock label="Updated By">{campaign.updatedBy?.name || campaign.updatedBy?.email}</InfoBlock>
              <InfoBlock label="Updated At">{campaign.updatedAt ? new Date(campaign.updatedAt).toLocaleString() : "—"}</InfoBlock>
              <Divider sx={{ my: 1 }} />
              <InfoBlock label="Published By">{campaign.publishedBy?.name || campaign.publishedBy?.email}</InfoBlock>
              <InfoBlock label="Published At">{campaign.publishedAt ? new Date(campaign.publishedAt).toLocaleString() : "—"}</InfoBlock>
              <InfoBlock label="Paused At">{campaign.pausedAt ? new Date(campaign.pausedAt).toLocaleString() : "—"}</InfoBlock>
              <InfoBlock label="Archived At">{campaign.archivedAt ? new Date(campaign.archivedAt).toLocaleString() : "—"}</InfoBlock>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                R2 Asset Information
              </Typography>
              {["en", "mr", "hi"].map((lang) =>
                campaign.creative?.[lang]?.objectKey ? (
                  <Box key={lang} sx={{ mb: 1.5 }}>
                    <Chip size="small" label={lang.toUpperCase()} sx={{ mb: 0.5 }} />
                    <Typography variant="caption" display="block" sx={{ wordBreak: "break-all", color: "text.secondary" }}>
                      {campaign.creative[lang].objectKey}
                    </Typography>
                    {campaign.creative[lang].width ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {campaign.creative[lang].width} × {campaign.creative[lang].height}px ·{" "}
                        {Math.round((campaign.creative[lang].fileSize || 0) / 1024)} KB
                      </Typography>
                    ) : null}
                  </Box>
                ) : null,
              )}
              {!["en", "mr", "hi"].some((lang) => campaign.creative?.[lang]?.objectKey) ? (
                <Typography variant="body2" color="text.secondary">
                  No images uploaded yet.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default AdvertisementDetail;
